// src/features/shared-scope/api/sharedScopeApi.ts
// ────────────────────────────────────────────────────────────────────────────
// Shared Scope API Layer — Zero-Trust HTTP Client
//
// All requests go through /bff/shared/:token/* — no session, no cookies needed.
// The token IS the credential. Defence in depth: no auth headers, no CSRF.
//
// DRY: mirrors the bff client pattern but stripped to the minimum needed
// for anonymous, zero-trust visitor access.
// ────────────────────────────────────────────────────────────────────────────

import { bff } from '@/lib/http/bff';

const BFF_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';

// ── Types ────────────────────────────────────────────────────────────────

/** Viewer configuration embedded at link creation time. */
export interface ViewerConfig {
  language?:    string;
  theme?:       string;
  /** Enable DSP signal cleaning on the viewer's telemetry plots. */
  dsp_enabled?: boolean;
  /** Subset of signals to render (scope_keys / metric types). Empty = all. */
  signals?:     string[];
  [key: string]: unknown;
}

/** Access level for shared links. */
export type SharedAccessLevel = 'public' | 'tenant_users' | 'selected_users';

/** Resolved shared link metadata (from /bff/shared/:token/resolve). */
export interface SharedLinkMeta {
  scope_type:     string;
  scope_ns:       string;
  scope_key:      string;
  scope_keys:     string[] | null;
  label:          string;
  permissions:    string[];
  tenant_id:      number;
  access_level:   SharedAccessLevel;
  viewer_config:  ViewerConfig | null;
  visitor: {
    fingerprint:  string;
    device_class: string;
    country:      string | null;
  };
}

// ── Digested Table-Ops Types ─────────────────────────────────────────────

export interface DigestedOrderLine {
  name:        string;
  qty:         number;
  price:       number;
  station:     string;
  description: string;
}

export interface DigestedTableOrder {
  id:           string | null;
  total:        number;
  finalized:    boolean;
  stationCount: number;
  lines:        DigestedOrderLine[];
}

/** Per-table guest state from live simulation engine. */
export interface DigestedTableGuest {
  stage:            string;
  covers:           number;
  round:            number;
  totalRounds:      number;
  subtotal:         number;
  itemCount:        number;
  wantsDessert:     boolean;
  isReservation:    boolean;
  paymentMethod:    string | null;
  stageMinutesLeft: number;
  merged:           boolean;
}

/** Simulation runtime info — only present on HOT lane (live sim engine). */
export interface SimulationRuntime {
  running:    boolean;
  paused:     boolean;
  simTime:    string;
  simMinute:  number;
  dayNumber:  number;
  occupancy:  number;
  guestCount: number;
  startedAt:  string | null;
  tenantName: string;
  timezone:   string;
  profile:    string;
}

export interface DigestedTable {
  key:    string;
  label:  string;
  seats:  number;
  shape:  string;
  status: 'free' | 'occupied' | 'has-order';
  order:  DigestedTableOrder | null;
  guest:  DigestedTableGuest | null;
  /** True when table exists in engine but has no floorplan entity. */
  ghost?: boolean;
}

export interface DigestedOrder {
  table:     string;
  orderId:   string | null;
  total:     number;
  finalized: boolean;
  lines:     DigestedOrderLine[];
}

export interface DigestedKdsLine {
  table:   string;
  name:    string;
  qty:     number;
  price:   number;
  status:  string;
  orderId: string | null;
}

export interface DigestedStats {
  totalTables: number;
  occupied:    number;
  withOrders:  number;
  free:        number;
  revenue:     number;
}

/** Table-ops scope data (from /bff/shared/:token/data). Pre-digested by BFF. */
export interface SharedTableOpsData {
  scope_type: 'table-ops';
  scope_ns:   string;
  scope_key:  string;
  tables:     DigestedTable[];
  orders:     DigestedOrder[];
  kds:        Record<string, DigestedKdsLine[]>;
  stats:      DigestedStats;
  menu:       Array<{ key: string; name: string; price: number; section: string; station: string; description: string }>;
  sections:   Array<{ key: string; name: string; order: number }>;
  /** Live simulation runtime state — only present when engine is running (HOT lane). */
  simulation: SimulationRuntime | null;
}

/** Generic scope data (fallback). */
export interface SharedGenericData {
  scope_type: 'generic';
  scope_ns:   string;
  scope_key:  string;
  items:      unknown[];
}

/** IoT multi-device scope data (from /bff/shared/:token/data). */
export interface SharedIoTDevicesData {
  scope_type:   'iot-devices';
  scope_ns:     string;
  scope_keys:   string[];
  devices:      unknown[];
  snapshots:    IoTSnapshotRow[];
  device_count: number;
}

/** IoT snapshot row (pivot — one row per device). */
export interface IoTSnapshotRow {
  scope_key:   string;
  temperature: number | null;
  humidity:    number | null;
  battery:     number | null;
  rssi:        number | null;
  power:       number | null;
  last_ts:     string | null;
}

export type SharedScopeData = SharedTableOpsData | SharedGenericData | SharedIoTDevicesData;

export class SharedApiError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'SharedApiError';
    this.status = status;
    this.code = code;
  }
}

// ── Core Fetch ───────────────────────────────────────────────────────────

async function sharedFetch<T>(token: string, path: string): Promise<T> {
  const url = `${BFF_ORIGIN}/bff/shared/${token}${path}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    // No credentials — zero-trust, token-only
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new SharedApiError(
      res.status,
      body.error || 'unknown',
      body.message || `Request failed with status ${res.status}`,
    );
  }

  return res.json() as Promise<T>;
}

// ── Public API ───────────────────────────────────────────────────────────

/** Resolve the shared link → returns rendering metadata. */
export async function resolveSharedLink(token: string): Promise<SharedLinkMeta> {
  return sharedFetch<SharedLinkMeta>(token, '/resolve');
}

/** Fetch scope data for the pinned scope. */
export async function fetchSharedData(token: string): Promise<SharedScopeData> {
  return sharedFetch<SharedScopeData>(token, '/data');
}

/** Fetch catalog entities (menu items, sections, etc.). */
export async function fetchSharedCatalog(token: string, namespace: string): Promise<{ items: unknown[] }> {
  return sharedFetch(token, `/catalog/${encodeURIComponent(namespace)}`);
}

/** Fetch orders for the pinned scope. */
export async function fetchSharedOrders(token: string): Promise<{ items: unknown[] }> {
  return sharedFetch(token, '/orders');
}

/** Single telemetry time-series data point. */
export interface SharedTelemetryPoint {
  ts:    number; // epoch-ms
  value: number;
}

/** Shape returned by GET /shared/:token/iot-telemetry. */
interface SharedTelemetrySeries {
  temp: SharedTelemetryPoint[];
  hum:  SharedTelemetryPoint[];
  rssi: SharedTelemetryPoint[];
}

/** Fetch IoT telemetry time-series for a specific device via shared link. */
export async function fetchSharedIoTTelemetry(
  token: string,
  scopeKey: string,
  opts?: { limit?: number; from?: string; to?: string },
): Promise<SharedTelemetrySeries> {
  const params = new URLSearchParams({ scope_key: scopeKey });
  if (opts?.limit) params.set('limit', String(opts.limit));
  if (opts?.from)  params.set('from', opts.from);
  if (opts?.to)    params.set('to', opts.to);
  return sharedFetch<SharedTelemetrySeries>(token, `/iot-telemetry?${params}`);
}

// ── Visitor Beacon ───────────────────────────────────────────────────────

/** Send visitor enrichment beacon (fire-and-forget). */
export function sendSharedBeacon(token: string): void {
  try {
    const payload = {
      screen_width:    window.screen.width,
      screen_height:   window.screen.height,
      viewport_width:  window.innerWidth,
      viewport_height: window.innerHeight,
      color_depth:     window.screen.colorDepth,
      timezone:        Intl.DateTimeFormat().resolvedOptions().timeZone,
      connection_type: (navigator as any).connection?.effectiveType || '',
    };

    // Use sendBeacon for reliability (survives page unload)
    const url = `${BFF_ORIGIN}/bff/shared/${token}/beacon`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, JSON.stringify(payload));
    } else {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never fail — analytics is best-effort
  }
}


// ── Admin API (authenticated, for link management) ──────────────────────

export interface SharedLinkRecord {
  token:            string;
  tenant_id:        number;
  scope_type:       string;
  scope_ns:         string;
  scope_key:        string;
  scope_keys:       string[] | null;
  permissions:      string[];
  label:            string;
  created_by:       string;
  created_at:       string;
  expires_at:       string | null;
  revoked:          boolean;
  access_count:     number;
  unique_visitors:  number;
  last_accessed_at: string | null;
  access_level:     SharedAccessLevel;
  allowed_user_ids: number[] | null;
  viewer_config:    ViewerConfig | null;
  url?:             string;
}

/** Create a new shared link (admin). */
export async function createSharedLink(params: {
  scope_type:       string;
  scope_ns:         string;
  scope_key?:       string;
  scope_keys?:      string[];
  permissions?:     string[];
  label?:           string;
  ttl_hours?:       number;
  tenant_id?:       number;
  access_level?:    SharedAccessLevel;
  allowed_user_ids?: number[];
  viewer_config?:   ViewerConfig;
}): Promise<SharedLinkRecord> {
  // Link creation now returns immediately after the in-memory cache write;
  // the orchestrator persist happens in the background on the BFF side.
  // 60 s timeout is a generous safety net in case the BFF itself cold-starts
  // (the old 30 s budget exactly matched the seed latency, causing races).
  return bff.post<SharedLinkRecord, typeof params>('/shared-links', params, { timeoutMs: 60_000 });
}

/** List shared links for current tenant (admin). */
export async function listSharedLinks(): Promise<{ items: SharedLinkRecord[]; total: number }> {
  return bff.get('/shared-links');
}

/** Revoke a shared link (admin). */
export async function revokeSharedLink(token: string): Promise<void> {
  return bff.del(`/shared-links/${token}`);
}

/** Get admin detail for a shared link (admin). */
export async function getSharedLinkAdmin(token: string): Promise<SharedLinkRecord> {
  return bff.get(`/shared-links/${token}/admin`);
}

// ── Visitor Forensics (admin, authenticated) ────────────────────────────

export interface SharedLinkVisitor {
  fingerprint:  string;
  is_new:       boolean;
  accessed_at:  string;
  ip:           string | null;
  country:      string | null;
  region:       string | null;
  city:         string | null;
  device_class: string | null;
  language:     string | null;
  referer:      string | null;
}

export interface SharedLinkVisitorData {
  visitors:   SharedLinkVisitor[];
  total_hits: number;
  unique:     number;
}

/** Get visitor forensic details for a shared link (admin). */
export async function getSharedLinkVisitors(token: string): Promise<SharedLinkVisitorData> {
  return bff.get(`/shared-links/${token}/visitors`);
}

/** Get the seeded FF demo link (admin discovery). */
export async function getDemoLink(): Promise<SharedLinkRecord> {
  return bff.get('/shared-links/demo');
}

// ── Shared Floor Plan Maps (visitor, zero-trust) ────────────────────────

/** Summary entry from GET /shared/:token/maps. */
export interface SharedFloorplanEntry {
  slug:       string;
  name:       string;
  savedAt:    string | null;
  tableCount: number;
  frameCount: number;
}

/** Full floor plan from GET /shared/:token/maps/:slug. */
export interface SharedSavedMap {
  name:    string;
  savedAt: string | null;
  view:    unknown;
  tables:  Array<{
    scopeKey: string; x: number; y: number; w: number; h: number;
    rotation: number; shape: string; label: string; seats: number; color: string;
  }>;
  frames:  Array<{
    scopeKey: string; points: Array<{ x: number; y: number }>;
    closed: boolean; color: string; label: string;
  }>;
}

/** Fetch the list of available floor plans for a table-ops shared link. */
export async function fetchSharedMaps(
  token: string,
): Promise<{ items: SharedFloorplanEntry[] }> {
  return sharedFetch<{ items: SharedFloorplanEntry[] }>(token, '/maps');
}

/** Fetch a single floor plan by slug. */
export async function fetchSharedMapBySlug(
  token: string,
  slug: string,
): Promise<SharedSavedMap> {
  return sharedFetch<SharedSavedMap>(token, `/maps/${encodeURIComponent(slug)}`);
}

// ── Activity Log Types & API (admin, authenticated) ─────────────────────

export type SharedLinkStatus = 'active' | 'expired' | 'revoked';

export interface ActivityLogEntry {
  token:            string;
  label:            string;
  status:           SharedLinkStatus;
  scope_type:       string;
  scope_ns:         string;
  scope_key:        string;
  scope_keys:       string[] | null;
  tenant_id:        number;
  permissions:      string[];
  created_by:       string;
  created_at:       string;
  expires_at:       string | null;
  access_level:     SharedAccessLevel;
  allowed_user_ids: number[] | null;
  viewer_config:    ViewerConfig | null;
  access_count:     number;
  unique_visitors:  number;
  last_accessed_at: string | null;
  url:              string | null;
}

export interface ActivityLogCounters {
  active:  number;
  expired: number;
  revoked: number;
}

export interface ActivityLogResponse {
  items:    ActivityLogEntry[];
  total:    number;
  counters: ActivityLogCounters;
}

/** Fetch the digested activity log for shared links (admin). */
export async function fetchSharedLinksActivity(): Promise<ActivityLogResponse> {
  return bff.get<ActivityLogResponse>('/shared-links/activity');
}

/** Recreate a shared link with the same parameters. */
export async function recreateSharedLink(entry: ActivityLogEntry): Promise<SharedLinkRecord> {
  return createSharedLink({
    scope_type:       entry.scope_type,
    scope_ns:         entry.scope_ns,
    scope_key:        entry.scope_key || undefined,
    scope_keys:       entry.scope_keys || undefined,
    permissions:      entry.permissions,
    label:            entry.label || undefined,
    tenant_id:        entry.tenant_id,
    access_level:     entry.access_level || undefined,
    allowed_user_ids: entry.allowed_user_ids || undefined,
    viewer_config:    entry.viewer_config || undefined,
  });
}

// ── SSE Event Types (from /bff/shared/:token/stream) ────────────────────

/** Full state snapshot pushed on connect and periodically (anti-drift). */
export interface SharedSseStateEvent {
  type: 'state';
  data: SharedTableOpsData;
}

/** Incremental sim event (table change, order, guest stage, kds, tick). */
export interface SharedSseSimEvent {
  type:      'sim_event';
  event:     string;
  simTime:   string;
  simMinute: number;
  day:       number;
  timezone:  string;
  ts:        string;
  [key: string]: unknown;
}

/** Cold-lane marker — indicates no live engine, FE should poll. */
export interface SharedSseColdEvent {
  type: 'cold';
}

/** Heartbeat keep-alive. */
export interface SharedSseHeartbeat {
  type: 'heartbeat';
  ts:   string;
}

export type SharedSseEvent =
  | SharedSseStateEvent
  | SharedSseSimEvent
  | SharedSseColdEvent
  | SharedSseHeartbeat;
